'use client';
import React, { useEffect, useRef } from 'react';

const collections = [
  {
    id: 'forked',
    label: '// most_forked',
    title: 'Most Forked Bases',
    description: 'The templates the community keeps cloning — battle-tested, production-ready, documented.',
    count: 24,
    icon: '⬡',
    accent: '#7C3AED',
    articles: [
      { title: 'Client Project Tracker v3', forked: 1247, difficulty: 'Beginner' },
      { title: 'Agency Retainer Base', forked: 891, difficulty: 'Intermediate' },
      { title: 'SaaS Metrics Dashboard', forked: 738, difficulty: 'Advanced' },
    ],
    span: 'lg:col-span-2',
  },
  {
    id: 'weekend',
    label: '// weekend_builds',
    title: 'Weekend Builds',
    description: 'Bite-sized projects completable in an afternoon. Ship something real before Sunday ends.',
    count: 18,
    icon: '◈',
    accent: '#8B5CF6',
    articles: [
      { title: 'Automated invoice reminder', forked: 456, difficulty: 'Beginner' },
      { title: 'Slack standup bot via automations', forked: 312, difficulty: 'Intermediate' },
    ],
    span: 'lg:col-span-1',
  },
  {
    id: 'scripting',
    label: '// advanced_series',
    title: 'Advanced Scripting Series',
    description: '8-part deep dive into production-grade Airtable scripting — async patterns, error handling, batch ops.',
    count: 8,
    icon: '◇',
    accent: '#6D28D9',
    articles: [
      { title: 'Part 1: Async/Await patterns', forked: 621, difficulty: 'Advanced' },
      { title: 'Part 2: Error boundaries', forked: 489, difficulty: 'Advanced' },
      { title: 'Part 3: Batch mutations', forked: 534, difficulty: 'Advanced' },
    ],
    span: 'lg:col-span-1',
  },
  {
    id: 'integrations',
    label: '// integrations_cookbook',
    title: 'Integrations Cookbook',
    description: 'Copy-paste recipes for Make, Zapier, n8n, and direct API calls. No fluff, just working configs.',
    count: 31,
    icon: '◉',
    accent: '#7C3AED',
    articles: [
      { title: 'Make → Airtable: field mapping guide', forked: 403, difficulty: 'Intermediate' },
      { title: 'n8n webhook to automation trigger', forked: 287, difficulty: 'Intermediate' },
    ],
    span: 'lg:col-span-2',
  },
];

const diffClass = (d: string) => ({
  Beginner: 'difficulty-beginner',
  Intermediate: 'difficulty-intermediate',
  Advanced: 'difficulty-advanced',
}[d] || '');

export default function CuratedCollections() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.fade-in-up').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 px-6 max-w-7xl mx-auto">
      <div className="mb-12 fade-in-up">
        <div className="flex items-center gap-3 mb-4">
          <span className="font-mono text-xs tracking-widest uppercase" style={{ color: '#52525B' }}>// curated</span>
          <div className="flex-1 h-px" style={{ background: 'rgba(124,58,237,0.15)' }} />
        </div>
        <h2 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ color: '#F4F4F5' }}>
          Curated collections
        </h2>
        <p className="text-sm mt-2" style={{ color: '#71717A' }}>
          Handpicked sequences for different stages of your Airtable journey
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {collections.map((col, i) => (
          <div key={col.id} className={`collection-card rounded-2xl p-6 cursor-pointer fade-in-up ${col.span}`}>
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div>
                <span className="font-mono text-xs mb-2 block" style={{ color: '#52525B' }}>{col.label}</span>
                <h3 className="text-lg font-bold" style={{ color: '#F4F4F5' }}>{col.title}</h3>
              </div>
              <span className="text-2xl" style={{ color: col.accent }}>{col.icon}</span>
            </div>

            <p className="text-sm leading-relaxed mb-5" style={{ color: '#71717A' }}>{col.description}</p>

            {/* Article list */}
            <div className="space-y-2 mb-5">
              {col.articles.map((art, j) => (
                <div key={j} className="flex items-center justify-between py-2 px-3 rounded-lg transition-colors"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}
                  onMouseEnter={e => (e.currentTarget as HTMLElement).style.borderColor = 'rgba(124,58,237,0.3)'}
                  onMouseLeave={e => (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.05)'}
                >
                  <span className="text-xs font-medium" style={{ color: '#A1A1AA' }}>{art.title}</span>
                  <div className="flex items-center gap-2 shrink-0 ml-2">
                    <span className={`tag-pill ${diffClass(art.difficulty)}`} style={{ fontSize: '9px', padding: '1px 6px' }}>
                      {art.difficulty}
                    </span>
                    <span className="font-mono text-[9px]" style={{ color: '#52525B' }}>⬡ {art.forked}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-3" style={{ borderTop: '1px solid rgba(124,58,237,0.1)' }}>
              <span className="font-mono text-xs" style={{ color: '#52525B' }}>{col.count} articles</span>
              <button className="text-xs font-semibold transition-colors" style={{ color: col.accent }}
                onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = '#C4B5FD'}
                onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = col.accent}
              >
                Browse collection →
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}